import { AlertCircle, Building2, Loader2, Search } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { companyPath } from "~/lib/company-profile";
import { MIN_COMPANY_SEARCH_LENGTH } from "~/lib/company-search";
import { trackEvent } from "~/lib/gtag";
import { useCompanyAutocomplete } from "~/lib/hooks/use-company-autocomplete";
import { cn } from "~/lib/utils";

interface CompanyAutocompleteProps {
	className?: string;
	formLabel?: string;
	initialValue?: string;
	isSearching?: boolean;
	onSearch?: (query: string) => void;
	placeholder?: string;
	showError?: boolean;
	size?: "compact" | "large";
	submitIconOnly?: boolean;
	submitLabel?: string;
}

/** Shared company search used everywhere a visitor can start a search. */
export function CompanyAutocomplete({
	className,
	formLabel = "Vyhledávání firem",
	initialValue = "",
	isSearching = false,
	onSearch,
	placeholder = "Název firmy nebo IČO",
	showError = true,
	size = "compact",
	submitIconOnly = false,
	submitLabel = "Hledat",
}: CompanyAutocompleteProps) {
	const navigate = useNavigate();
	const generatedId = useId().replaceAll(":", "");
	const listboxId = `company-search-${generatedId}-listbox`;
	const errorId = `company-search-${generatedId}-error`;
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const [query, setQuery] = useState(initialValue);
	const [isOpen, setIsOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(-1);
	const { suggestions, status, error } = useCompanyAutocomplete(query);
	const canSearch = query.trim().length >= MIN_COMPANY_SEARCH_LENGTH;
	const showSuggestions = isOpen && suggestions.length > 0;

	useEffect(() => {
		setQuery(initialValue);
		setIsOpen(false);
		setActiveIndex(-1);
	}, [initialValue]);

	useEffect(() => {
		if (status !== "success") return;
		setActiveIndex(-1);
		setIsOpen(
			suggestions.length > 0 &&
				typeof document !== "undefined" &&
				document.activeElement === inputRef.current,
		);
	}, [status, suggestions]);

	useEffect(() => {
		const handlePointerDown = (event: PointerEvent) => {
			if (!containerRef.current?.contains(event.target as Node)) {
				setIsOpen(false);
				setActiveIndex(-1);
			}
		};

		document.addEventListener("pointerdown", handlePointerDown);
		return () => document.removeEventListener("pointerdown", handlePointerDown);
	}, []);

	const selectSuggestion = (index: number) => {
		const suggestion = suggestions[index];
		if (!suggestion) return;
		setQuery(suggestion.name);
		setIsOpen(false);
		setActiveIndex(-1);
		inputRef.current?.blur();
		navigate(companyPath(suggestion.ico, suggestion.name));
	};

	const submitSearch = () => {
		const value = query.trim();
		if (value.length < MIN_COMPANY_SEARCH_LENGTH) return;
		setIsOpen(false);
		setActiveIndex(-1);
		inputRef.current?.blur();
		trackEvent("search", { search_term: value });
		if (onSearch) {
			onSearch(value);
			return;
		}
		navigate(`/search?q=${encodeURIComponent(value)}`);
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Escape") {
			setIsOpen(false);
			setActiveIndex(-1);
			return;
		}

		if (suggestions.length === 0) return;
		if (event.key === "ArrowDown") {
			event.preventDefault();
			setIsOpen(true);
			setActiveIndex((current) => (current < suggestions.length - 1 ? current + 1 : 0));
		} else if (event.key === "ArrowUp") {
			event.preventDefault();
			setIsOpen(true);
			setActiveIndex((current) => (current > 0 ? current - 1 : suggestions.length - 1));
		} else if (event.key === "Home" && showSuggestions) {
			event.preventDefault();
			setActiveIndex(0);
		} else if (event.key === "End" && showSuggestions) {
			event.preventDefault();
			setActiveIndex(suggestions.length - 1);
		} else if (event.key === "Enter" && showSuggestions && activeIndex >= 0) {
			event.preventDefault();
			selectSuggestion(activeIndex);
		}
	};

	return (
		<div
			ref={containerRef}
			className={cn("relative", className)}
			onBlurCapture={(event) => {
				if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
					setIsOpen(false);
					setActiveIndex(-1);
				}
			}}
		>
			{/* biome-ignore lint/a11y/useSemanticElements: React does not recognize the HTML search element yet. */}
			<form
				role="search"
				aria-label={formLabel}
				onSubmit={(event) => {
					event.preventDefault();
					submitSearch();
				}}
				className={cn(
					"glass-control flex min-w-0 gap-2 p-1.5",
					size === "large"
						? "flex-col rounded-2xl sm:flex-row sm:items-center sm:rounded-full"
						: "items-center rounded-full",
				)}
			>
				<div className="relative min-w-0 flex-1">
					<Search
						aria-hidden="true"
						className={cn(
							"pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-primary-600",
							size === "large" ? "h-5 w-5" : "h-4 w-4",
						)}
					/>
					<input
						ref={inputRef}
						type="search"
						value={query}
						onChange={(event) => {
							setQuery(event.target.value);
							setIsOpen(false);
							setActiveIndex(-1);
						}}
						onFocus={() => suggestions.length > 0 && setIsOpen(true)}
						onKeyDown={handleKeyDown}
						placeholder={placeholder}
						autoComplete="off"
						spellCheck={false}
						role="combobox"
						aria-label="Název firmy nebo IČO"
						aria-autocomplete="list"
						aria-expanded={showSuggestions}
						aria-controls={listboxId}
						aria-activedescendant={
							activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined
						}
						aria-describedby={error ? errorId : undefined}
						aria-busy={status === "loading"}
						className={cn(
							"w-full rounded-full bg-transparent pl-10 text-sm text-text outline-none placeholder:text-gray-400 focus:ring-2 focus:ring-primary-200",
							size === "large" ? "h-12 pr-11" : "h-10 pr-9",
						)}
					/>
					{status === "loading" && (
						<Loader2
							className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-primary-600"
							aria-label="Načítám návrhy"
						/>
					)}
				</div>

				<button
					type="submit"
					disabled={!canSearch || isSearching}
					aria-label={isSearching ? "Hledám firmy" : submitLabel}
					aria-busy={isSearching}
					className={cn(
						"btn btn-primary shrink-0 rounded-full px-5 text-white shadow-sm",
						size === "large" ? "h-12 w-full sm:w-auto" : "h-10",
						submitIconOnly && "w-10 px-0",
					)}
				>
					{isSearching ? (
						<Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
					) : (
						<Search className={cn("h-4 w-4", !submitIconOnly && "sm:hidden")} aria-hidden="true" />
					)}
					<span
						className={cn(size === "compact" && "hidden sm:inline", submitIconOnly && "sr-only")}
					>
						{isSearching ? "Hledám…" : submitLabel}
					</span>
				</button>
			</form>

			{showError && status === "error" && error && (
				<div
					id={errorId}
					role="alert"
					className="mt-2 flex items-start gap-2 px-2 text-xs leading-5 text-muted-foreground"
				>
					<AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-error" aria-hidden="true" />
					<span>{error}</span>
				</div>
			)}

			{showSuggestions && (
				<div
					id={listboxId}
					role="listbox"
					aria-label="Návrhy firem"
					className="scrollbar-none absolute left-0 right-0 z-50 mt-2 max-h-80 overflow-y-auto rounded-2xl bg-card py-2 shadow-pop"
				>
					{suggestions.map((suggestion, index) => (
						<button
							type="button"
							key={suggestion.ico}
							id={`${listboxId}-option-${index}`}
							role="option"
							aria-selected={index === activeIndex}
							onMouseEnter={() => setActiveIndex(index)}
							onMouseDown={(event) => event.preventDefault()}
							onClick={() => selectSuggestion(index)}
							className={cn(
								"flex w-full items-center gap-3 px-4 py-3 text-left transition-colors",
								index === activeIndex ? "bg-primary-50" : "hover:bg-gray-50",
							)}
						>
							<span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50">
								<Building2 className="h-4 w-4 text-primary-600" aria-hidden="true" />
							</span>
							<span className="min-w-0">
								<span className="block truncate text-sm font-medium text-text">
									{suggestion.name}
								</span>
								<span className="block truncate text-xs text-muted">
									IČO: <span className="font-mono">{suggestion.ico}</span>
									{suggestion.city && ` · ${suggestion.city}`}
								</span>
							</span>
						</button>
					))}
				</div>
			)}
		</div>
	);
}
